import type { Components } from '@mui/material';
import { gridClasses } from '@mui/x-data-grid-pro';

type ThemeOption = {
  colors: {
    link: string;
    linkHover: string;
    textDark?: string;
    primaryText?: string;
    grey50: string;
    grey100: string;
    grey300: string;
    grey400: string;
    grey700: string;
    primaryLight: string;
    truentityBlue: { A200: string };
  };
  paper: string;
  textDark: string;
  darkTextPrimary: string;
  darkTextSecondary: string;
  menuSelected: string;
  menuSelectedBack: string;
  divider: string;
  customization: {
    borderRadius: number;
  };
};

export function componentStyleOverrides(theme: ThemeOption): Components | undefined {
  return {
    MuiCssBaseline: {
      styleOverrides: {
        a: {
          color: theme.colors.link,
          '&:hover': {
            color: theme.colors.linkHover
          }
        }
      }
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true
      },
      styleOverrides: {
        root: {
          fontWeight: 500,
          borderRadius: theme.customization.borderRadius + 'px',
          textTransform: 'uppercase',
          '&.MuiButton-text.MuiButton-sizeSmall': {
            padding: 0
          },
          '&.MuiButton-text.MuiButton-sizeMedium': {
            padding: 0
          },
          '&.MuiButton-text.MuiButton-sizeLarge': {
            padding: 0
          }
        },
        sizeSmall: {
          padding: '6px 16px'
        },
        sizeMedium: {
          padding: '8px 20px'
        },
        sizeLarge: {
          padding: '11px 24px'
        }
      }
    },
    MuiPaper: {
      defaultProps: {
        elevation: 1
      },
      styleOverrides: {
        root: {
          backgroundImage: 'none'
        },
        rounded: {
          borderRadius: theme.customization.borderRadius + 'px'
        }
      }
    },
    MuiCardHeader: {
      styleOverrides: {
        root: {
          color: theme.colors.textDark,
          padding: '24px'
        },
        title: {
          fontSize: '1.125rem'
        }
      }
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '24px'
        }
      }
    },
    MuiCardActions: {
      styleOverrides: {
        root: {
          padding: '24px'
        }
      }
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          textTransform: 'capitalize',
          color: theme.darkTextPrimary,
          borderRadius: theme.customization.borderRadius + 'px',
          padding: '10px 16px 10px 24px',
          marginBottom: '4px',
          '&.Mui-selected': {
            color: theme.menuSelected,
            backgroundColor: theme.menuSelectedBack,
            '&:hover': {
              backgroundColor: theme.menuSelectedBack
            },
            '& .MuiListItemIcon-root': {
              color: theme.menuSelected
            }
          },
          '&:hover': {
            backgroundColor: theme.menuSelectedBack,
            color: theme.menuSelected,
            '& .MuiListItemIcon-root': {
              color: theme.menuSelected
            }
          }
        }
      }
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          color: theme.colors.grey700,
          minWidth: '22px',
          svg: {
            fontSize: '1.1rem'
          }
        }
      }
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          color: theme.textDark,
          '&::placeholder': {
            color: theme.darkTextSecondary,
            fontSize: '0.875rem'
          }
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          background: theme.colors.grey50,
          borderRadius: theme.customization.borderRadius + 'px',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.colors.grey400
          },
          '&:hover $notchedOutline': {
            borderColor: theme.colors.primaryLight
          },
          '&.MuiInputBase-multiline': {
            padding: 1
          },
          '&.MuiInputBase-sizeSmall > .MuiInputBase-input': {
            padding: '10px 14px'
          },
          '&.MuiInputBase-sizeSmall.MuiInputBase-adornedStart > .MuiInputBase-input': {
            paddingLeft: 0
          }
        },
        input: {
          background: theme.colors.grey50,
          padding: '15.5px 14px',
          borderRadius: theme.customization.borderRadius + 'px'
        },
        adornedStart: {
          paddingLeft: 4
        },
        notchedOutline: {
          borderRadius: theme.customization.borderRadius + 'px'
        }
      }
    },
    // Pickers (date/time fields) don't route through MuiOutlinedInput in v8 - they mount
    // MuiPickersOutlinedInput on their own accessible field structure, so the overrides above
    // never reach them. Mirrored here so picker fields still match ordinary text inputs.
    MuiPickersInputBase: {
      styleOverrides: {
        root: {
          color: theme.textDark,
          '&.MuiPickersInputBase-adornedStart': {
            paddingLeft: 4
          }
        }
      }
    },
    MuiPickersOutlinedInput: {
      styleOverrides: {
        root: {
          background: theme.colors.grey50,
          borderRadius: theme.customization.borderRadius + 'px',
          '& .MuiPickersOutlinedInput-notchedOutline': {
            borderColor: theme.colors.grey400
          },
          '&:hover .MuiPickersOutlinedInput-notchedOutline': {
            borderColor: theme.colors.primaryLight
          }
        },
        // Vertical padding only, on purpose. MuiOutlinedInput above puts both axes on the input
        // element, but MUI gives a picker field its horizontal padding on the *root*
        // (`padding: '0 14px'` in PickersOutlinedInput) and only vertical padding here
        // (`16.5px 0`, or `8.5px 0` when small). Mirroring the OutlinedInput value verbatim
        // therefore applied 14px twice per side and cost the value 28px of usable width - enough
        // to clip a date range in the 220px `sm` field on the RPM Readings page. The
        // small+adornedStart `paddingLeft: 0` rule that used to sit here went with it: it only
        // existed to cancel the horizontal padding that is no longer applied.
        sectionsContainer: {
          padding: '15.5px 0',
          '.MuiPickersInputBase-inputSizeSmall &': {
            padding: '10px 0'
          }
        },
        notchedOutline: {
          borderRadius: theme.customization.borderRadius + 'px'
        }
      }
    },
    // Hour/minute pickers open a MultiSectionDigitalClock only 112px wide, but PickersLayout
    // reserves grid column 1 for a shortcuts panel this app never renders and then sizes that
    // column from the widest row - the action bar (Cancel + OK = 152px). The 40px surplus lands
    // in the empty column as a dead strip to the left of the clock. Dropping the action bar lets
    // the popper shrink to the clock's natural width: a value is already committed the moment an
    // item is clicked, so OK only closed the popper. defaultProps is safe here because MUI's
    // resolveProps merges `slotProps` per slot key, so call sites passing `slotProps.textField`
    // keep this default - and any that set their own `actionBar` still win.
    //
    // Keyed to MuiDesktopTimePicker, NOT MuiTimePicker. `TimePicker` is a responsive wrapper: it
    // resolves the `MuiTimePicker` key itself and only then branches on `(pointer: fine)` to render
    // DesktopTimePicker or MobileTimePicker, so that key configures both. The mobile variant is a
    // modal dialog around an analog clock, where the action bar is the only way out - an empty
    // `actions` array left it with no buttons at all. This key is resolved by DesktopTimePicker on
    // top of the wrapper's, so `<TimePicker>` on a fine pointer still gets the tightened popper
    // while the mobile modal keeps Cancel/OK. DatePicker keeps Clear and DateTimePicker keeps
    // next/nextOrAccept either way.
    MuiDesktopTimePicker: {
      defaultProps: {
        slotProps: {
          actionBar: {
            actions: []
          }
        }
      }
    },
    // A meridiem section holds two items (AM/PM) but flex-stretches to the height of the 12-item
    // hours column, leaving ~163px empty below PM. Shrink-wrap each section instead. The hours
    // column is unaffected: its items still overflow the 232px DIGITAL_CLOCK_VIEW_HEIGHT cap, so
    // it keeps its height and its hover-to-scroll behaviour.
    MuiMultiSectionDigitalClockSection: {
      styleOverrides: {
        root: {
          alignSelf: 'flex-start'
        }
      }
    },
    MuiSlider: {
      styleOverrides: {
        root: {
          '&.Mui-disabled': {
            color: theme.colors.grey300
          }
        },
        mark: {
          backgroundColor: theme.paper,
          width: '4px'
        },
        valueLabel: {
          color: theme.colors.primaryLight
        }
      }
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: theme.divider,
          opacity: 1
        }
      }
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          color: theme.colors.primaryText,
          background: theme.colors.truentityBlue.A200
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: {
          '&.MuiChip-deletable .MuiChip-deleteIcon': {
            color: 'inherit'
          }
        }
      }
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          color: theme.paper,
          background: theme.colors.grey700
        }
      }
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: theme.colors.grey100,
          borderBottom: 'none',
          '& .MuiTableCell-root': {
            borderBottom: 'none',
            fontWeight: 500,
            lineHeight: 1,
            letterSpacing: 0.5,
            textTransform: 'uppercase'
          },
          '& .MuiTableCell-paddingCheckbox': {
            paddingTop: 4,
            paddingBottom: 4
          }
        }
      }
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          // `contentFiller` is an internal MUI X flow-spacer rendered as a bare <div>: MUI ships no
          // CSS for it at all and relies on it being `position: static`. The global
          // `* { position: relative }` reset in public/critical.css promotes it into the positioned
          // layer, where - as a later sibling of the absolutely-positioned render zone that holds the
          // rows, both at `z-index: auto` - it paints over every row and swallows all clicks. Putting
          // it back in the static flow restores row interaction without affecting its height, which
          // is what makes it a spacer.
          //
          // Deliberately NOT extended to `.MuiDataGrid-filler`, the other post-render-zone sibling MUI
          // declares no `position` for. Measured both ways in the browser: it is a 15px strip below
          // all the content (rect 1680x15 at y=3301 while the rows occupy y=362-939), so it overlaps
          // no row and is never the hit target whether it is `static` or `relative`. `contentFiller`
          // is dangerous precisely because it stands in for the rows' height and so covers exactly
          // their rectangle; `filler` only carries the last-row border and the scrollbar gutter.
          // Note the class is also shared with a column-headers element (useGridColumnHeaders), so a
          // blanket rule would reach more than it looks like it does.
          [`& .${gridClasses.contentFiller}`]: {
            position: 'static'
          }
        },
        panel: {
          maxWidth: 'calc(100vw - 32px)',
          '& .MuiDataGrid-filterForm': {
            flexWrap: 'wrap',
            rowGap: 8
          }
        }
      }
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          borderBottom: 'none'
        }
      }
    }
  };
}
